/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useReducer, useRef, useState, ReactNode } from 'react'
import { RcmTableDataContext, RcmTableDataContextValue } from './RcmTableDataContext'
import persistService from '../../services/persist'

// Constants matching the JSX version
const GENERIC_TABLE_PERSIST_KEY = 'genericTable'
const DEFAULT_PER_PAGE = 15
const DEFAULT_SORT_BY = 'NAME'
const TABLE_SORT_DIR = {
  asc: 'asc',
  desc: 'desc'
} as const

interface RcmTableDataWrapperProps {
  children: ReactNode
  loadData: (
    params: any,
    filter?: any
  ) => Promise<{
    elements: any[]
    totalElements: number
    hasNextPage?: boolean
  }>
  persistenceKey?: string
  defaultSortBy?: string
  defaultSortDesc?: boolean
  initialPage?: number
  updateUrlPageParam?: boolean
  doPreload?: boolean
  reloadController?: (refreshFn: () => void) => void
  requireFilter?: boolean // If true, prevents load/sort operations when filter is empty
}

interface TableState {
  total: number
  entries: any[]
  hasNextPage: boolean
  isLoading: boolean
  error: boolean
}

const initialState: TableState = {
  total: 0,
  entries: [],
  hasNextPage: false,
  isLoading: false,
  error: false
}

const REDUCER_ACTIONS = {
  LOAD_INIT: 'LOAD_INIT',
  LOAD_SUCCESS: 'LOAD_SUCCESS',
  LOAD_ERROR: 'LOAD_ERROR'
} as const

type ReducerAction =
  | { type: 'LOAD_INIT' }
  | { type: 'LOAD_SUCCESS'; payload: { elements: any[]; totalElements: number; hasNextPage?: boolean } }
  | { type: 'LOAD_ERROR' }

function reducer(state: TableState, action: ReducerAction): TableState {
  switch (action.type) {
    case REDUCER_ACTIONS.LOAD_INIT:
      return {
        ...state,
        error: false,
        hasNextPage: false,
        isLoading: true
      }
    case REDUCER_ACTIONS.LOAD_SUCCESS:
      return {
        isLoading: false,
        total: action.payload?.totalElements || initialState.total,
        entries: action.payload?.elements || initialState.entries,
        hasNextPage: typeof action.payload?.hasNextPage === 'boolean' ? action.payload.hasNextPage : false,
        error: false
      }
    case REDUCER_ACTIONS.LOAD_ERROR:
      return {
        ...initialState,
        error: true
      }
    default:
      return state
  }
}

export const RcmTableDataWrapper: React.FC<RcmTableDataWrapperProps> = ({
  children,
  loadData,
  persistenceKey = GENERIC_TABLE_PERSIST_KEY,
  defaultSortBy,
  defaultSortDesc,
  initialPage = 1,
  updateUrlPageParam = false,
  doPreload,
  reloadController,
  requireFilter = false
}) => {
  // Initialize loading state based on whether we'll preload data
  const initialLoadingState = {
    ...initialState,
    isLoading: doPreload ?? false
  }

  const [state, dispatch] = useReducer(reducer, initialLoadingState)
  const [filter, setFilter] = useState<any>({})
  const numberOfLoads = useRef(0)
  // Keep a ref to the latest loadData so stale closures (e.g. from reloadController)
  // always call the current version rather than the one captured at mount time.
  const loadDataRef = useRef(loadData)
  loadDataRef.current = loadData
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false)
  const gridViewPersistenceKey = persistenceKey + '_gridView'
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [isGridView, setIsGridViewInternal] = useState(false)
  const [currentSortBy, setCurrentSortBy] = useState(defaultSortBy || 'id')
  const [currentSortDesc, setCurrentSortDesc] = useState(defaultSortDesc)

  // Load persisted pagination settings
  const getPersistedPerPage = (): number => {
    const persistedPaginateSettings = persistService.getGlobal<{
      page: number
      perPage: number
    }>('paginate')
    if (persistedPaginateSettings && persistedPaginateSettings.perPage) {
      return persistedPaginateSettings.perPage
    }
    return DEFAULT_PER_PAGE
  }

  const setIsGridView = (newVal: boolean) => {
    persistService.setGlobal(gridViewPersistenceKey, newVal)
    setIsGridViewInternal(newVal)
  }

  function applyFilter(filterData: any) {
    setFilter(filterData)
  }

  function refreshTable(goToPage: number | null = null, isSilent = false, userId: number | null = null) {
    const paginatorSettings = persistService.getGlobal<{ page: number; perPage: number }>('paginate')
    const sortSettings = persistService.getBasic<{ sort: string; sortDir: string }>(persistenceKey)
    const pageStart =
      goToPage !== null ? goToPage : paginatorSettings && paginatorSettings.page ? paginatorSettings.page : 1
    const pageSize = paginatorSettings && paginatorSettings.perPage ? paginatorSettings.perPage : DEFAULT_PER_PAGE

    if (goToPage !== null && !isSilent) {
      persistService.setGlobal('paginate', { page: goToPage, perPage: pageSize })
    }

    let loadParams: any = {
      pageStart: Math.max(0, pageStart - 1),
      pageSize: pageSize,
      sortBy: sortSettings && sortSettings.sort ? sortSettings.sort : defaultSortBy || DEFAULT_SORT_BY,
      sortDesc: sortSettings && sortSettings.sortDir ? sortSettings.sortDir === TABLE_SORT_DIR.desc : !!defaultSortDesc
    }

    if (userId) {
      loadParams = { ...loadParams, userId: userId }
    }

    load(loadParams, isSilent)
  }

  const load = (pagingParams: any, isSilent = false) => {
    // Don't load data if filter is empty when requireFilter is true
    // This prevents API calls when user hasn't clicked Filter button (for tables that require filters)
    if (requireFilter && Object.keys(filter).length === 0) {
      return
    }

    if (!isSilent) {
      setCurrentPage(pagingParams.pageStart + 1)
      setCurrentSortBy(pagingParams.sortBy)
      setCurrentSortDesc(pagingParams.sortDesc)
      numberOfLoads.current = numberOfLoads.current + 1

      dispatch({
        type: REDUCER_ACTIONS.LOAD_INIT
      })
    }

    try {
      loadDataRef
        .current(pagingParams, filter)
        .then((result) => {
          if (isSilent && JSON.stringify(result.elements) === JSON.stringify(state.entries)) {
            // when it is silent table refresh (eg. websocket/polling) and the result is the same as it is currently rendered,
            // don't update UI
            return
          }

          dispatch({
            type: REDUCER_ACTIONS.LOAD_SUCCESS,
            payload: {
              elements: result.elements,
              totalElements: result.totalElements,
              ...(typeof result.hasNextPage === 'boolean' ? { hasNextPage: result.hasNextPage } : {})
            }
          })
        })
        .catch((e) => {
          dispatch({
            type: REDUCER_ACTIONS.LOAD_ERROR
          })
          console.error('Error occurred when calling Rcm Table Load Data function', e)
        })
    } catch (e) {
      dispatch({
        type: REDUCER_ACTIONS.LOAD_ERROR
      })
      console.error(
        'Error in Rcm Table Load Data function. It must return a promise with object of {elements: Array<any>,  totalElements: Number}'
      )
    }
  }

  useEffect(() => {
    if (Object.keys(filter).length) {
      refreshTable(numberOfLoads.current === 0 ? initialPage : 1)
    }
  }, [filter])

  useEffect(() => {
    const gridViewPersistedSettings = persistService.getGlobal<boolean>(gridViewPersistenceKey)
    setIsGridView(typeof gridViewPersistedSettings === 'boolean' ? gridViewPersistedSettings : false)

    if (reloadController) {
      reloadController(refreshTable)
    }

    if (doPreload) {
      refreshTable(initialPage)
    }
  }, [])

  const setSort = (newSortBy: string, newSortDesc: boolean) => {
    // Don't sort if no filter is applied yet (when requireFilter is true)
    if (requireFilter && Object.keys(filter).length === 0) {
      return
    }

    setCurrentPage(1)

    // Persist sort settings
    persistService.setBasic(persistenceKey, {
      sort: newSortBy,
      sortDir: newSortDesc ? TABLE_SORT_DIR.desc : TABLE_SORT_DIR.asc
    })

    // Refresh table with new sort on page 1
    const pageSize = getPersistedPerPage()
    load({
      pageStart: 0,
      pageSize: pageSize,
      sortBy: newSortBy,
      sortDesc: newSortDesc
    })
  }

  const tableApi: RcmTableDataContextValue = {
    load: load,
    refreshTable: refreshTable,
    persistenceKey: persistenceKey,
    total: state.total,
    hasNextPage: state.hasNextPage,
    page: currentPage,
    filter,
    applyFilter,
    isLoading: state.isLoading,
    entries: state.entries,
    error: state.error,
    defaultSortBy: defaultSortBy || null,
    defaultSortDesc: defaultSortDesc || false,
    sortBy: currentSortBy,
    sortDesc: currentSortDesc || false,
    setSort,
    setFilter: applyFilter,
    isFilterCollapsed,
    setIsFilterCollapsed,
    updateUrlPageParam,
    isGridView,
    setIsGridView
  }

  return <RcmTableDataContext.Provider value={tableApi}>{children}</RcmTableDataContext.Provider>
}
